import { useSelector } from 'react-redux';

import type { ReviewsSummaryProps } from 'src/components/organisms/ReviewsSummary/components/ReviewsSummary';
import { getAsAdvertisedPercentage } from 'src/components/organisms/ReviewsSummary/helpers/getAsAdvertisedPercentage';
import {
  trackAiSummaryImpression,
  trackMostLovedImpression,
  trackReviewsSummaryImpression,
} from 'src/components/organisms/ReviewsSummary/tracking/reviewsSummaryEvents';
import { selectProductAttributes } from 'src/redux/product/selectors';
import {
  selectAiSummary,
  selectListAverageRating,
  selectProductInsights,
  selectRatingBreakdown,
} from 'src/redux/reviews/selectors';
import type { ReviewsSummaryAiSummary } from 'src/redux/reviews/types';

/**
 * View model for the reviews summary. Owns the reads across the product and
 * reviews slices, the minimum-responses rule on the "% as advertised" score
 * and the three impressions, so the presentational section stays pure props.
 *
 * Returns null only when there is nothing at all to show, which is how the
 * section is omitted from the page. Without an AI summary the heading, the
 * rating, the Most Loved pill and the as-advertised score still stand on their own.
 */
export interface ReviewsSummaryViewModel extends Omit<ReviewsSummaryProps, 'aiSummary'> {
  aiSummary: ReviewsSummaryAiSummary | null;
}

export const useReviewsSummaryViewModel = (): ReviewsSummaryViewModel | null => {
  const aiSummary = useSelector(selectAiSummary);
  const productInsights = useSelector(selectProductInsights);
  const attributes = useSelector(selectProductAttributes);
  const ratingBreakdown = useSelector(selectRatingBreakdown);
  const listAverageRating = useSelector(selectListAverageRating);

  const asAdvertisedPercentage = getAsAdvertisedPercentage(productInsights);
  const isMostLoved = Boolean(attributes?.highlyRated);
  /*
    Feefo's average, falling back to the review list's own. Both settle after hydration,
    so the rating appears once one of them lands rather than in the server markup. The
    page's `aggregateRating` structured data deliberately stays on UPS, which exists
    during SSR when neither does. See getAggregateRatingJSONLDSchema.
  */
  const averageRating = ratingBreakdown?.averageRating || listAverageRating || null;

  if (!aiSummary && averageRating === null && asAdvertisedPercentage === null && !isMostLoved) {
    return null;
  }

  return {
    aiSummary: aiSummary ?? null,
    asAdvertisedPercentage,
    averageRating,
    isMostLoved,
    onAiSummaryVisible: trackAiSummaryImpression,
    onMostLovedVisible: trackMostLovedImpression,
    onVisible: trackReviewsSummaryImpression,
  };
};
